/* ---- API fetch wrapper ---- */

// B-1 構成: SPA と API は別 origin。VITE_API_BASE_URL でビルド時に origin を埋め込む。
// dev (= 未設定) では空のまま相対パスで Vite proxy → localhost:4000 にルーティングされる。
const API_ORIGIN = import.meta.env.VITE_API_BASE_URL ?? '';
const BASE_URL = `${API_ORIGIN}/api/v1`;

class ApiService {
  private accessToken: string | null = null;

  setToken(token: string | null): void {
    this.accessToken = token;
  }

  getToken(): string | null {
    return this.accessToken;
  }

  private headers(): Record<string, string> {
    const h: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.accessToken) {
      h.Authorization = `Bearer ${this.accessToken}`;
    }
    return h;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    params?: Record<string, string>,
    isRetry = false,
  ): Promise<T> {
    let url = `${BASE_URL}${path}`;
    if (params) {
      const qs = new URLSearchParams(params).toString();
      if (qs) url += `?${qs}`;
    }

    const res = await fetch(url, {
      method,
      headers: this.headers(),
      body: body ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401 && !isRetry) {
      // Token expired, try refresh (only once)
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        return this.request<T>(method, path, body, params, true);
      }
      // Refresh failed — clear tokens
      this.setToken(null);
      localStorage.removeItem('ga_access_token');
      localStorage.removeItem('ga_refresh_token');
      throw new ApiError(401, 'UNAUTHORIZED', '認証が必要です');
    }

    if (res.status === 401 && isRetry) {
      this.setToken(null);
      localStorage.removeItem('ga_access_token');
      localStorage.removeItem('ga_refresh_token');
      throw new ApiError(401, 'UNAUTHORIZED', '認証が必要です');
    }

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new ApiError(
        res.status,
        data?.error?.code || 'UNKNOWN',
        data?.error?.message || res.statusText,
      );
    }
    return res.json();
  }

  private async tryRefresh(): Promise<boolean> {
    const refreshToken = localStorage.getItem('ga_refresh_token');
    if (!refreshToken) return false;

    try {
      const res = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!res.ok) return false;

      const data = await res.json();
      this.setToken(data.access_token);
      localStorage.setItem('ga_access_token', data.access_token);
      return true;
    } catch {
      return false;
    }
  }

  async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    return this.request<T>('GET', path, undefined, params);
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, body);
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = new ApiService();
